import { useEffect, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { StatusCard } from '@/components/cards';

function getStatusChipProps(loading: boolean, error: string | null, hasIssues: boolean) {
  if (loading) return { label: 'Loading', variant: 'secondary' as const };
  if (error) return { label: 'Error', variant: 'destructive' as const };
  if (hasIssues) return { label: 'Attention', variant: 'destructive' as const };
  return { label: 'OK', variant: 'default' as const };
}

export function DashboardPage() {
  const { hasPageAccess } = useAuth();
  const [easycronFailing, setEasycronFailing] = useState<number | null>(null);
  const [easycronLoading, setEasycronLoading] = useState<boolean>(false);
  const [easycronError, setEasycronError] = useState<string | null>(null);
  const [site24x7Down, setSite24x7Down] = useState<number | null>(null);
  const [site24x7Trouble, setSite24x7Trouble] = useState<number | null>(null);
  const [site24x7Critical, setSite24x7Critical] = useState<number | null>(null);
  const [site24x7Loading, setSite24x7Loading] = useState<boolean>(false);
  const [site24x7Error, setSite24x7Error] = useState<string | null>(null);
  const [dmaMonitorStatus, setDmaMonitorStatus] = useState<'healthy' | 'warning' | 'alert' | null>(null);
  const [dmaMonitorMessage, setDmaMonitorMessage] = useState<string | null>(null);
  const [dmaMonitorLoading, setDmaMonitorLoading] = useState<boolean>(false);
  const [dmaMonitorError, setDmaMonitorError] = useState<string | null>(null);
  const [cycleUnsyncedCount, setCycleUnsyncedCount] = useState<number | null>(null);
  const [cycleLoading, setCycleLoading] = useState<boolean>(false);
  const [cycleError, setCycleError] = useState<string | null>(null);
  const [walletErrorsCount, setWalletErrorsCount] = useState<number | null>(null);
  const [walletErrorsLoading, setWalletErrorsLoading] = useState<boolean>(false);
  const [walletErrorsError, setWalletErrorsError] = useState<string | null>(null);
  const [pubsubStatus, setPubsubStatus] = useState<'healthy' | 'warning' | 'alert' | null>(null);
  const [pubsubAlertCount, setPubsubAlertCount] = useState<number | null>(null);
  const [pubsubWarningCount, setPubsubWarningCount] = useState<number | null>(null);
  const [pubsubLoading, setPubsubLoading] = useState<boolean>(false);
  const [pubsubError, setPubsubError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFailing = async () => {
      if (!hasPageAccess('monitoring')) return;
      setEasycronLoading(true);
      setEasycronError(null);
      try {
        const resp = await api.get('/monitoring/easycron/jobs');
        const data = resp.data?.data;
        const list = Array.isArray(data?.cron_jobs)
          ? data.cron_jobs
          : (Array.isArray(resp.data?.cron_jobs) ? resp.data.cron_jobs : []);
        const failing = list.filter((j: any) => Number(j?.current_failures) > 0).length;
        setEasycronFailing(failing);
      } catch (e: any) {
        setEasycronError('Failed to load EasyCron status');
      } finally {
        setEasycronLoading(false);
      }
    };
    fetchFailing();
  }, [hasPageAccess]);

  useEffect(() => {
    const fetchSite24x7 = async () => {
      if (!hasPageAccess('monitoring')) return;
      setSite24x7Loading(true);
      setSite24x7Error(null);
      try {
        const resp = await api.get('/monitoring/site24x7/current-status');
        const outer = resp.data?.data ?? {};
        const data = outer?.data ?? outer;
        const counts = data?.monitors_count;
        if (counts) {
          setSite24x7Down(counts.down || 0);
          setSite24x7Trouble(counts.trouble || 0);
          setSite24x7Critical(counts.critical || 0);
        }
      } catch (e: any) {
        setSite24x7Error('Failed to load Site24x7 status');
      } finally {
        setSite24x7Loading(false);
      }
    };
    fetchSite24x7();
  }, [hasPageAccess]);

  useEffect(() => {
    const fetchDmaMonitor = async () => {
      if (!hasPageAccess('monitoring')) return;
      setDmaMonitorLoading(true);
      setDmaMonitorError(null);
      try {
        const resp = await api.get('/monitoring/dma-monitor/status');
        const data = resp.data?.data;
        if (data) {
          setDmaMonitorStatus(data.status || 'healthy');
          setDmaMonitorMessage(data.message || 'Status unknown');
        }
      } catch (e: any) {
        setDmaMonitorError('Failed to load DMA monitor status');
      } finally {
        setDmaMonitorLoading(false);
      }
    };
    fetchDmaMonitor();
  }, [hasPageAccess]);

  useEffect(() => {
    const fetchCycleUnsynced = async () => {
      if (!hasPageAccess('cycle')) return;
      setCycleLoading(true);
      setCycleError(null);
      try {
        const resp = await api.get('/cycle/unsynced-count');
        const data = resp.data?.data ?? resp.data;
        const count = data?.count ?? 0;
        setCycleUnsyncedCount(count);
      } catch (e: any) {
        setCycleError('Failed to load cycle sync status');
      } finally {
        setCycleLoading(false);
      }
    };
    fetchCycleUnsynced();
  }, [hasPageAccess]);

  useEffect(() => {
    const fetchWalletErrors = async () => {
      if (!hasPageAccess('wallet')) return;
      setWalletErrorsLoading(true);
      setWalletErrorsError(null);
      try {
        const resp = await api.get('/wallet/failures');
        const data = resp.data?.data ?? resp.data;
        const count = data?.count ?? 0;
        setWalletErrorsCount(count);
      } catch (e: any) {
        setWalletErrorsError('Failed to load wallet errors');
      } finally {
        setWalletErrorsLoading(false);
      }
    };
    fetchWalletErrors();
  }, [hasPageAccess]);

  useEffect(() => {
    const fetchPubsubStatus = async () => {
      if (!hasPageAccess('monitoring')) return;
      setPubsubLoading(true);
      setPubsubError(null);
      try {
        const resp = await api.get('/monitoring/pubsub/status');
        const data = resp.data?.data;
        if (data) {
          const status = data.overallStatus;
          if (status) {
            setPubsubStatus(status);
            setPubsubAlertCount(data.alertCount ?? 0);
            setPubsubWarningCount(data.warningCount ?? 0);
          } else {
            try {
              await api.post('/monitoring/pubsub/status/refresh');
              const refreshResp = await api.get('/monitoring/pubsub/status');
              const refreshData = refreshResp.data?.data;
              if (refreshData?.overallStatus) {
                setPubsubStatus(refreshData.overallStatus);
                setPubsubAlertCount(refreshData.alertCount ?? 0);
                setPubsubWarningCount(refreshData.warningCount ?? 0);
              }
            } catch (refreshErr) {
              console.error('Failed to refresh Pub/Sub status:', refreshErr);
            }
          }
        }
      } catch (e: any) {
        console.error('Error fetching Pub/Sub status:', e);
        setPubsubError('Failed to load Pub/Sub status');
      } finally {
        setPubsubLoading(false);
      }
    };
    fetchPubsubStatus();
    const interval = setInterval(fetchPubsubStatus, 30000);
    return () => clearInterval(interval);
  }, [hasPageAccess]);

  const site24x7HasIssues = (site24x7Down ?? 0) > 0 || (site24x7Trouble ?? 0) > 0 || (site24x7Critical ?? 0) > 0;
  const site24x7IssueCount = (site24x7Down ?? 0) + (site24x7Trouble ?? 0) + (site24x7Critical ?? 0);
  const dmaMonitorHasIssues = dmaMonitorStatus === 'warning' || dmaMonitorStatus === 'alert';
  const cycleHasIssues = (cycleUnsyncedCount ?? 0) > 0;
  const walletErrorsHasIssues = (walletErrorsCount ?? 0) > 0;
  const pubsubHasIssues = pubsubStatus === 'warning' || pubsubStatus === 'alert';

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome to Strata"
      />

      {(hasPageAccess('monitoring') || hasPageAccess('cycle') || hasPageAccess('wallet')) && (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <StatusCard
              title="EasyCron Health"
              message={
                easycronLoading
                  ? 'Loading…'
                  : (easycronFailing ?? 0) > 0
                    ? `${easycronFailing} job${(easycronFailing ?? 0) === 1 ? '' : 's'} failing`
                    : 'All jobs healthy'
              }
              loading={easycronLoading}
              error={easycronError}
              href="/monitoring/easycron"
              statusChip={getStatusChipProps(easycronLoading, easycronError, (easycronFailing ?? 0) > 0)}
            />
          </div>

          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <StatusCard
              title="Site24x7 Status"
              message={
                site24x7Loading
                  ? 'Loading…'
                  : site24x7HasIssues
                    ? `${site24x7IssueCount} monitor${site24x7IssueCount === 1 ? '' : 's'} with issues`
                    : 'All monitors healthy'
              }
              loading={site24x7Loading}
              error={site24x7Error}
              href="/monitoring/site24x7"
              statusChip={getStatusChipProps(site24x7Loading, site24x7Error, site24x7HasIssues)}
            />
          </div>

          <div className="col-span-12 sm:col-span-6 md:col-span-4">
            <StatusCard
              title="DMA/MSISDN Monitor"
              message={dmaMonitorLoading ? 'Loading…' : dmaMonitorMessage || 'Status unknown'}
              loading={dmaMonitorLoading}
              error={dmaMonitorError}
              href="/monitoring/dma-monitor"
              statusChip={getStatusChipProps(dmaMonitorLoading, dmaMonitorError, dmaMonitorHasIssues)}
            />
          </div>

          {hasPageAccess('cycle') && (
            <div className="col-span-12 sm:col-span-6 md:col-span-4">
              <StatusCard
                title="Cycle Sync Status"
                message={
                  cycleLoading
                    ? 'Loading…'
                    : cycleHasIssues
                      ? `${cycleUnsyncedCount} agent${(cycleUnsyncedCount ?? 0) === 1 ? '' : 's'} unsynced`
                      : 'All agents synced'
                }
                loading={cycleLoading}
                error={cycleError}
                href="/cycle/agent-sync"
                statusChip={getStatusChipProps(cycleLoading, cycleError, cycleHasIssues)}
              />
            </div>
          )}

          {hasPageAccess('wallet') && (
            <div className="col-span-12 sm:col-span-6 md:col-span-4">
              <StatusCard
                title="Wallet Errors"
                message={
                  walletErrorsLoading
                    ? 'Loading…'
                    : walletErrorsHasIssues
                      ? `${walletErrorsCount} error${(walletErrorsCount ?? 0) === 1 ? '' : 's'}`
                      : 'No wallet errors'
                }
                loading={walletErrorsLoading}
                error={walletErrorsError}
                href="/wallet-failures"
                statusChip={getStatusChipProps(walletErrorsLoading, walletErrorsError, walletErrorsHasIssues)}
              />
            </div>
          )}

          {hasPageAccess('monitoring') && (
            <div className="col-span-12 sm:col-span-6 md:col-span-4">
              <StatusCard
                title="Pub/Sub Status"
                message={
                  pubsubLoading
                    ? 'Loading…'
                    : pubsubHasIssues
                      ? `${pubsubAlertCount ?? 0} alert${(pubsubAlertCount ?? 0) === 1 ? '' : 's'}, ${pubsubWarningCount ?? 0} warning${(pubsubWarningCount ?? 0) === 1 ? '' : 's'}`
                      : 'All subscriptions healthy'
                }
                loading={pubsubLoading}
                error={pubsubError}
                href="/monitoring/pubsub"
                statusChip={getStatusChipProps(pubsubLoading, pubsubError, pubsubHasIssues)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
